import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";

// Shows the top referrers (by accepted-count desc, then total desc) so the
// founder can hand-thank community champions. Falls back to a warm empty
// state until the first real referral arrives.
export default function ReferralLeaderboard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/admin/referrals/leaderboard").then((r) => {
            if (alive) setRows(Array.isArray(r.data) ? r.data : []);
        }).catch(() => {
            // Non-fatal: leaderboard is optional context above the main list.
        }).finally(() => {
            if (alive) setLoading(false);
        });
        return () => { alive = false; };
    }, []);

    if (loading) return null;

    if (!rows.length) {
        return (
            <div className="border border-[color:var(--espresso)] p-6 mb-8" data-testid="referral-leaderboard-empty">
                <div className="flex items-center gap-2 text-[color:var(--gold)]">
                    <Sparkles size={14} />
                    <p className="eyebrow">Referral leaderboard</p>
                </div>
                <p className="mt-3 text-sm text-[color:var(--cream-muted)]">Once applicants start sharing their invite codes, your top community champions will surface here.</p>
            </div>
        );
    }

    const visible = expanded ? rows : rows.slice(0, 5);
    return (
        <div className="border border-[color:var(--gold)]/60 bg-[color:var(--gold)]/5 p-6 mb-8" data-testid="referral-leaderboard">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[color:var(--gold)]">
                    <Sparkles size={14} />
                    <p className="eyebrow">Referral leaderboard · top {rows.length}</p>
                </div>
                {rows.length > 5 && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-[0.65rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] hover:text-[color:var(--gold)] flex items-center gap-1"
                        data-testid="leaderboard-toggle"
                    >
                        {expanded ? (<>Collapse <ChevronUp size={12} /></>) : (<>Show all {rows.length} <ChevronDown size={12} /></>)}
                    </button>
                )}
            </div>
            <div className="mt-4 divide-y divide-[color:var(--espresso)]/60">
                {visible.map((r, i) => (
                    <div key={r.referral_code} className="flex items-center gap-4 py-3" data-testid={`leaderboard-row-${r.referral_code}`}>
                        <span className="font-display text-2xl text-[color:var(--gold)]/60 w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-[color:var(--soft-cream)] truncate">
                                {r.first_name || "—"} {r.last_name || ""}
                                {r.referrer_status && r.referrer_status !== "submitted" && (
                                    <span className="ml-2 text-[0.6rem] uppercase tracking-[0.18em] text-[color:var(--cream-muted)]">· {r.referrer_status}</span>
                                )}
                            </p>
                            <p className="text-[0.65rem] uppercase tracking-[0.16em] text-[color:var(--cream-muted)] font-mono truncate">
                                {r.referral_code} · {r.email || "—"}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="font-display text-2xl text-[color:var(--gold)] leading-none" data-testid={`leaderboard-accepted-${r.referral_code}`}>{r.accepted}</p>
                            <p className="text-[0.58rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] mt-1">accepted / {r.referrals}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
